import { useState, useEffect, useRef, useCallback } from 'react';
import './HorizontalScrollbar.scss';

function HorizontalScrollbar({ scrollRef, className }) {
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const dragState = useRef(null);
  const [thumbStyle, setThumbStyle] = useState({ width: 0, left: 0 });
  const [visible, setVisible] = useState(false);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (scrollWidth <= clientWidth) {
      setVisible(false);
      return;
    }

    setVisible(true);
    const track = trackRef.current;
    if (!track) return;

    const trackWidth = track.clientWidth;
    const ratio = clientWidth / scrollWidth;
    const thumbWidth = Math.max(ratio * trackWidth, 30);
    const maxThumbLeft = trackWidth - thumbWidth;
    const scrollRatio = scrollLeft / (scrollWidth - clientWidth);
    const thumbLeft = scrollRatio * maxThumbLeft;

    setThumbStyle({ width: thumbWidth, left: thumbLeft });
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', update);
    const observer = new ResizeObserver(update);
    observer.observe(el);

    // Also observe children for content size changes
    for (const child of el.children) {
      observer.observe(child);
    }

    update();

    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [scrollRef, update]);

  useEffect(() => {
    if (visible) update();
  }, [visible, update]);

  const scrollTo = useCallback(
    (thumbLeft) => {
      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      const trackWidth = track.clientWidth;
      const ratio = el.clientWidth / el.scrollWidth;
      const thumbWidth = Math.max(ratio * trackWidth, 30);
      const maxThumbLeft = trackWidth - thumbWidth;
      const scrollRatio = Math.max(0, Math.min(1, thumbLeft / maxThumbLeft));
      el.scrollLeft = scrollRatio * (el.scrollWidth - el.clientWidth);
    },
    [scrollRef],
  );

  const handleThumbMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      dragState.current = { startX: e.clientX, startLeft: thumbStyle.left };
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (e) => {
        if (!dragState.current) return;
        const delta = e.clientX - dragState.current.startX;
        scrollTo(dragState.current.startLeft + delta);
      };

      const handleMouseUp = () => {
        dragState.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [thumbStyle.left, scrollTo],
  );

  const handleTrackClick = useCallback(
    (e) => {
      if (e.target === thumbRef.current) return;
      const track = trackRef.current;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const ratio =
        scrollRef.current.clientWidth / scrollRef.current.scrollWidth;
      const thumbWidth = Math.max(ratio * track.clientWidth, 30);
      scrollTo(clickX - thumbWidth / 2);
    },
    [scrollRef, scrollTo],
  );

  const handleChevronClick = useCallback(
    (direction) => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({
        left: direction * el.clientWidth * 0.75,
        behavior: 'smooth',
      });
    },
    [scrollRef],
  );

  if (!visible) return null;

  return (
    <div
      className={'horizontal-scrollbar' + (className ? ' ' + className : '')}
    >
      <button
        className='scroll-btn fa-chevron-left transparent'
        onClick={() => handleChevronClick(-1)}
        aria-label='Scroll left'
      />
      <div className='scroll-track' ref={trackRef} onClick={handleTrackClick}>
        <div
          className='scroll-thumb'
          ref={thumbRef}
          style={{ width: thumbStyle.width, left: thumbStyle.left }}
          onMouseDown={handleThumbMouseDown}
        />
      </div>
      <button
        className='scroll-btn fa-chevron-right transparent'
        onClick={() => handleChevronClick(1)}
        aria-label='Scroll right'
      />
    </div>
  );
}

export default HorizontalScrollbar;
